/** How a dashboard's cards change when it is brought into step with the server. */
export type CardSyncPlan<T> = {
    /**
     * The dashboard's cards in the server's order. A card still on the
     * dashboard is kept as it is here, taking only the server's order, so
     * nothing the user changed on it in this session is lost. A new card is
     * taken as the server sent it.
     */
    signalsObjects: T[];
    addedIds: string[];
    removedIds: string[];
};

export function planCardSync<T extends { id: string; order?: number }>(
    current: readonly T[],
    server: readonly T[],
): CardSyncPlan<T> {
    const currentById = new Map(current.map((card) => [card.id, card]));
    const serverIds = new Set(server.map((card) => card.id));

    const signalsObjects = [...server]
        .sort(
            (a, b) =>
                (a.order ?? Number.MAX_SAFE_INTEGER) -
                (b.order ?? Number.MAX_SAFE_INTEGER),
        )
        .map((card) => {
            const existing = currentById.get(card.id);
            return existing ? { ...existing, order: card.order } : card;
        });

    return {
        signalsObjects,
        addedIds: server
            .filter((card) => !currentById.has(card.id))
            .map((card) => card.id),
        removedIds: current
            .filter((card) => !serverIds.has(card.id))
            .map((card) => card.id),
    };
}
